import { concat, fn, hash } from '@ember/helper';
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import { t } from 'ember-intl';
import { trackedFunction } from 'reactiveweb/function';

import Component from 'umg-ember-backend/components/record-explorer';
import Table from 'umg-ember-backend/components/record-explorer/table';
import Toolbar from 'umg-ember-backend/components/record-explorer/toolbar';
import Modal from 'umg-ember-backend/components/ui/modal';
import Pagination from 'umg-ember-backend/components/ui/pagination';
import Window from 'umg-ember-backend/components/ui/window';
import { query } from 'umg-ember-backend/data/builders/umg-rest';

import Templates from './templates';

export default class RecordExplorerForPageBuilderComponent extends Component {
  // Services
  @service data;

  // Defaults
  @tracked showTemplateSelect = false;

  defaultColumns = [
    'id',
    'headline',
    'artist',
    'visibleLiveUrl',
    'cmsLanguageId',
    'cmsState',
    'cmsModified',
  ];

  tableColumns = [
    {
      property: 'id',
      sortable: true,
    },
    {
      property: 'headline',
      sortable: true,
      isMainColumn: true,
      isTruncate: true,
    },
    {
      property: 'artist',
      type: 'relation',
    },
    {
      property: 'visibleLiveUrl',
      isTruncate: true,
      type: 'link',
    },
    {
      property: 'previewUrl',
      isTruncate: true,
    },
    {
      property: 'version',
      sortable: true,
    },
    {
      property: 'shopifyStore',
      type: 'relation',
    },
    {
      property: 'cmsLanguageId',
      sortable: true,
      type: 'translate-value',
    },
    {
      property: 'cmsState',
      sortable: true,
      type: 'translate-value',
      align: 'right',
    },
    {
      property: 'cmsModified',
      sortable: true,
      type: 'date',
      format: 'L LT',
      align: 'right',
    },
    {
      property: 'cmsCreated',
      sortable: true,
      type: 'date',
      format: 'L LT',
      align: 'right',
    },
  ];

  get availableFilters() {
    return [
      ...this.defaultFilters,
      {
        key: 'shopifyStore',
        type: 'selectRelationship',
        recordType: 'shopify-store',
        queryParams: { 'sort[storeId]': 'ASC' },
        options: [
          {
            key: this.intl.t('tables.mlp.filterOptions.with-shopify-store'),
            value: true,
          },
          {
            key: this.intl.t('tables.mlp.filterOptions.without-shopify-store'),
            value: false,
          },
        ],
      },
    ];
  }

  // Getter and setter
  get templates() {
    return this.loadTemplates.value ?? [];
  }

  // Functions
  loadTemplates = trackedFunction(this, async () => {
    const { content } = await this.store.request(
      query('mlp', {
        'filter[cmsState]': 2,
        'filter[isTemplate]': 1,
        'limit': 40,
        'offset': 0,
        'sort[cmsModified]': 'DESC',
      }),
    );

    return content.data ?? [];
  });

  // Actions
  toggleTemplateSelect = () => {
    this.showTemplateSelect = !this.showTemplateSelect;
  };

  previewTemplate = (template) => {
    template.preview();
  };

  // Template
  // We need to overwrite the default template, because user can select a page template instead of directly creating a record
  <template>
    <Window
      @isBottombarVisible={{if this.recordsData.length true false}}
      @isLoading={{this.loadRecords.isRunning}}
      @isSeamless={{true}}
      @onBodyScroll={{this.setScrollPosition}}
      @scrollPosition={{this.scrollPosition}}
      {{didInsert this.setOrLoadRecords}}
    >
      <:title>
        {{t (concat 'models.plurals.' this.recordType)}}
      </:title>

      <:toolbar>
        <Toolbar
          @activeColumns={{this.activeColumns}}
          @activeFilters={{this.activeFilters}}
          @availableFilters={{this.availableFilters}}
          @columns={{this.tableColumns}}
          @recordType={{this.recordType}}
          @onAddRecord={{this.toggleTemplateSelect}}
          @onAssignRelation={{@onAssignRelation}}
          @onSearch={{this.search}}
          @onSetFilter={{this.setFilter}}
          @parentRecordType={{@parentRecordType}}
          @searchString={{this.searchString}}
          @selectedRecords={{this.selectedRecords}}
        />
      </:toolbar>

      <:body>
        <Table
          @activeSort={{this.activeSort}}
          @columns={{this.visibleColumns}}
          @records={{this.recordsData}}
          @recordType={{this.recordType}}
          @recordActions={{hash
            edit=this.loadRecord
            duplicate=this.duplicateRecord
            delete=this.deleteRecord
          }}
          @selectedRecords={{this.selectedRecords}}
          @onAssignRelation={{@onAssignRelation}}
          @onUpdateSort={{fn this.setFilter 'sort'}}
          @onSelectRecord={{this.selectRecord}}
        />

        <Modal
          @onClose={{this.toggleTemplateSelect}}
          @dataDependencies={{this.showTemplateSelect}}
        >
          <Templates
            @addFromTemplate={{this.duplicateRecord}}
            @addRecord={{this.addRecord}}
            @previewTemplate={{this.previewTemplate}}
            @templates={{this.templates}}
          />
        </Modal>
      </:body>

      <:bottombar>
        <Pagination
          @pagination={{this.records.meta}}
          @setPage={{this.setPage}}
          @setPageLimit={{fn this.setFilter 'pageSize'}}
        />
      </:bottombar>
    </Window>
  </template>
}
